import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import {
  Trash2, Loader2, AlertTriangle, RefreshCw, FileImage, FileText,
  CheckCircle2, Square, CheckSquare, AlertOctagon, Zap,
} from "lucide-react";
import { toast } from "sonner";

function fmtBytes(n) {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function relTime(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  if (diff < 60_000) return "just now";
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86400_000) return `${Math.floor(diff / 3600_000)}h ago`;
  if (diff < 2592000_000) return `${Math.floor(diff / 86400_000)}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function StorageCleanupAdmin() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("orphans"); // all | orphans | referenced
  const [purgeStatus, setPurgeStatus] = useState(null);
  const [backendStats, setBackendStats] = useState(null);
  const pollRef = useRef(null);

  const loadPurgeStatus = async () => {
    try {
      const r = await api.get("/admin/storage/purge-status");
      setPurgeStatus(r.data);
      return r.data;
    } catch { return null; }
  };

  const loadBackendStats = async () => {
    try {
      const r = await api.get("/admin/storage/backend-stats", { timeout: 60_000 });
      setBackendStats(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not list storage backend");
    }
  };

  // Poll every 2s while a purge is running so the progress bar moves live.
  useEffect(() => {
    if (purgeStatus?.status === "running") {
      pollRef.current = setInterval(loadPurgeStatus, 2000);
      return () => clearInterval(pollRef.current);
    }
    if (purgeStatus?.status === "done" && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
      // Refresh both lists once the purge finishes
      load();
      loadBackendStats();
    }
  }, [purgeStatus?.status]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/storage/list");
      setData(r.data);
      // Pre-select all orphans so the most-common bulk-delete is one click
      setSelected(new Set(r.data.items.filter((it) => !it.referenced).map((it) => it.id)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load storage list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    loadPurgeStatus();
    loadBackendStats();
  }, []);

  const items = data?.items || [];
  const visible = useMemo(() => {
    if (filter === "orphans") return items.filter((it) => !it.referenced);
    if (filter === "referenced") return items.filter((it) => it.referenced);
    return items;
  }, [items, filter]);

  const toggle = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const selectAllVisible = () => setSelected(new Set(visible.map((it) => it.id)));
  const clearSelection = () => setSelected(new Set());

  const selectedItems = items.filter((it) => selected.has(it.id));
  const selectedSize = selectedItems.reduce((s, it) => s + (it.size || 0), 0);
  const selectedReferenced = selectedItems.filter((it) => it.referenced).length;

  const startBgPurge = async () => {
    if (!data) return;
    const orphans = data.orphan_count;
    if (!orphans) return toast.info("No orphans to purge.");
    const ok = window.confirm(
      `Delete ${orphans} orphan file(s) tracked in the database?\n\n` +
      `Estimated reclaimable: ${fmtBytes(data.orphan_size)}\n\n` +
      `Runs server-side — you can close this tab and come back later. ` +
      `Progress is saved on the server.\n\n` +
      `This cannot be undone.`
    );
    if (!ok) return;
    try {
      const r = await api.post("/admin/storage/purge-start", { only_orphans: true });
      if (r.data.already_running) {
        toast.info("A purge is already running.");
      } else {
        toast.success("Purge started — you can close this tab.");
      }
      await loadPurgeStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start purge");
    }
  };

  const startStorageOrphanPurge = async () => {
    if (!backendStats) return;
    const orphans = backendStats.orphan_count;
    if (!orphans) return toast.info("No storage orphans to purge.");
    const ok = window.confirm(
      `🚨 STORAGE-LEVEL CLEANUP\n\n` +
      `Delete ${orphans.toLocaleString()} ghost files directly from Emergent Object Storage?\n\n` +
      `These are files that exist in storage but have no database row pointing to them ` +
      `(usually leftover from old WordPress imports or interrupted bulk-deletes).\n\n` +
      `This will reclaim approximately ${fmtBytes(backendStats.orphan_size)} of quota.\n\n` +
      `Runs server-side — close this tab and come back. CANNOT be undone.`
    );
    if (!ok) return;
    try {
      const r = await api.post("/admin/storage/purge-start", { storage_orphans: true });
      if (r.data.already_running) {
        toast.info("A purge is already running.");
      } else {
        toast.success("Storage-level purge started — you can close this tab.");
      }
      await loadPurgeStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start purge");
    }
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    const totalBytes = selectedSize;
    const ok = window.confirm(
      `Delete ${selected.size} file(s)?\n\n` +
      `This will reclaim ~${fmtBytes(totalBytes)} of Object Storage quota.\n\n` +
      (selectedReferenced > 0
        ? `${selectedReferenced} of these are still referenced — they will be SKIPPED to keep the site working.\n\n`
        : "") +
      `This cannot be undone.`
    );
    if (!ok) return;
    setBusy(true);
    // Drive the deletion in 200-file chunks so the browser sees progress
    // every few seconds instead of one long-running request that can hit
    // the Cloudflare/proxy timeout when storage is slow.
    const CHUNK = 200;
    const queue = [...selected];
    const toastId = toast.loading(`Deleting 0 / ${queue.length}…`, { duration: Infinity });
    let totalDeleted = 0;
    let totalSkipped = 0;
    let totalFreed = 0;
    try {
      for (let i = 0; i < queue.length; i += CHUNK) {
        const slice = queue.slice(i, i + CHUNK);
        // Long timeout — the backend caps each file at 5s and runs 8 in
        // parallel, so 200 files finishes in ~30 s on the worst day.
        const r = await api.post(
          "/admin/storage/bulk-delete",
          { ids: slice, skip_referenced: true, chunk_size: CHUNK },
          { timeout: 90_000 }
        );
        totalDeleted += r.data.deleted || 0;
        totalSkipped += r.data.skipped_referenced || 0;
        totalFreed += r.data.bytes_freed || 0;
        toast.loading(
          `Deleting ${Math.min(i + CHUNK, queue.length)} / ${queue.length}…  (${fmtBytes(totalFreed)} freed)`,
          { id: toastId, duration: Infinity }
        );
      }
      toast.success(
        `Deleted ${totalDeleted}${totalSkipped ? ` · ${totalSkipped} kept (in use)` : ""} · ${fmtBytes(totalFreed)} freed`,
        { id: toastId }
      );
      await load();
    } catch (e) {
      toast.error(
        `Stopped after ${totalDeleted} files (${fmtBytes(totalFreed)} freed). ${e?.response?.data?.detail || e?.message || ""}`,
        { id: toastId, duration: 8000 }
      );
      // Reload anyway so the admin sees the partial progress
      await load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-storage-page">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">Maintenance</div>
        <h1 className="font-serif text-4xl mt-2 text-ink">Storage Cleanup</h1>
        <p className="mt-2 text-ink/60 text-sm max-w-3xl">
          Every uploaded image (event flyers, hall photos, page covers, etc.) lives in Emergent Object Storage.
          Orphans are files that aren't used anywhere on the site — they're the safest to delete and reclaim quota.
        </p>
      </div>

      {/* STORAGE-BACKEND PANEL — counts what's REALLY on Emergent Object
          Storage (not just MongoDB) and offers a one-click ghost-file purge.
          IMPORTANT: this purge ONLY touches files that have no db.uploads
          row at all — anything visible in the grid below is safe. */}
      {backendStats && (
        <div
          className={`rounded-3xl p-6 md:p-8 mb-6 relative overflow-hidden ${
            backendStats.orphan_count > 0
              ? "bg-gradient-to-br from-rose-700 to-rose-900 text-cream"
              : "bg-emerald-700 text-cream"
          }`}
          data-testid="backend-stats-panel"
        >
          <div className="absolute inset-0 pattern-girih opacity-10 pointer-events-none" />
          <div className="relative flex flex-wrap items-center justify-between gap-5">
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-[0.25em] text-cream/85 font-semibold inline-flex items-center gap-1.5">
                <AlertOctagon size={11} /> Storage backend (true usage)
              </div>
              <h2 className="font-serif text-2xl mt-1.5">
                {backendStats.orphan_count > 0
                  ? `${backendStats.orphan_count.toLocaleString()} ghost files eating your quota`
                  : "Storage backend is clean"}
              </h2>
              <p className="mt-2 text-cream/85 text-sm leading-relaxed">
                {backendStats.total_count.toLocaleString()} object{backendStats.total_count === 1 ? "" : "s"} occupying{" "}
                <strong>{fmtBytes(backendStats.total_size)}</strong> on Emergent Object Storage.
                {backendStats.orphan_count > 0 && (
                  <>
                    {" "}<strong>{fmtBytes(backendStats.orphan_size)}</strong> is reclaimable — ghost files leftover from
                    old WP imports or interrupted bulk-deletes that have no database row pointing to them.
                  </>
                )}
              </p>
              {backendStats.orphan_count > 0 && (
                <div className="mt-3 inline-flex items-start gap-2 bg-cream/10 border border-cream/20 rounded-xl px-3 py-2 text-[12px] leading-relaxed">
                  <CheckCircle2 size={14} className="shrink-0 mt-0.5 text-emerald-300" />
                  <span>
                    <strong>Safe by design:</strong> this only deletes files <em>not</em> shown on this page.
                    Anything visible in the file grid below stays untouched — those uploads are still
                    yours to use whenever you need them.
                  </span>
                </div>
              )}
              {Object.keys(backendStats.by_prefix || {}).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {Object.entries(backendStats.by_prefix).map(([k, v]) => (
                    <span key={k} className="text-[11px] bg-cream/10 border border-cream/20 px-2.5 py-1 rounded-full">
                      {k}: <span className="font-semibold">{v.count.toLocaleString()}</span> · {fmtBytes(v.size)}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="shrink-0 flex flex-col gap-2">
              <button
                onClick={loadBackendStats}
                data-testid="backend-stats-refresh"
                className="inline-flex items-center gap-2 rounded-full bg-cream/10 border border-cream/20 text-cream px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-cream/15"
              >
                <RefreshCw size={12} /> Re-scan storage
              </button>
              {backendStats.orphan_count > 0 && (
                <button
                  onClick={startStorageOrphanPurge}
                  disabled={purgeStatus?.status === "running"}
                  data-testid="storage-orphan-purge"
                  className="inline-flex items-center gap-2 rounded-full bg-cream text-rose-700 px-6 py-3 text-xs font-bold uppercase tracking-wider hover:bg-cream/90 disabled:opacity-40 disabled:cursor-not-allowed shadow-lg"
                >
                  <Zap size={14} /> Purge {backendStats.orphan_count.toLocaleString()} ghosts
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      {data && (
        <div className="grid sm:grid-cols-3 gap-4 mb-8" data-testid="storage-stats">
          <Stat icon={FileImage} label="Total files" value={data.total_count} subtle={fmtBytes(data.total_size)} />
          <Stat icon={AlertOctagon} label="Orphans" value={data.orphan_count} subtle={`${fmtBytes(data.orphan_size)} reclaimable`} highlight />
          <Stat icon={CheckCircle2} label="In use" value={data.total_count - data.orphan_count} subtle="protected" />
        </div>
      )}

      {/* DB-ORPHAN BULK PURGE — DELETES the orphans visible on this page.
          Hidden by default since the user is most likely keeping those files
          for upcoming events; only revealed when the admin explicitly opens
          the danger zone. */}
      {(purgeStatus?.status === "running" || purgeStatus?.status === "done" ||
        (data && data.orphan_count >= 50)) && (
        <details className="mb-8 group" data-testid="bg-purge-details">
          <summary className="cursor-pointer list-none">
            <div className="rounded-2xl border-2 border-dashed border-rose-300 bg-rose-50 px-5 py-3.5 inline-flex items-center gap-2.5 text-rose-700 text-xs font-semibold uppercase tracking-wider hover:bg-rose-100 transition-colors">
              <AlertTriangle size={14} />
              Danger zone — delete the {(data?.orphan_count || 0).toLocaleString()} files shown below
              <span className="ml-1 text-rose-500 group-open:rotate-180 transition-transform">⌄</span>
            </div>
          </summary>
          <div className="mt-3 rounded-3xl bg-gradient-to-br from-navy to-navy-900 text-cream p-6 md:p-8 relative overflow-hidden" data-testid="bg-purge-panel">
            <div className="absolute inset-0 pattern-girih opacity-10 pointer-events-none" />
            <div className="relative flex flex-wrap items-center justify-between gap-5">
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-[0.25em] text-rose-300 font-semibold inline-flex items-center gap-1.5">
                  <Zap size={11} /> Bulk purge · DELETES VISIBLE FILES
                </div>
                <h2 className="font-serif text-2xl mt-1.5">
                  {purgeStatus?.status === "running" ? "Purging in the background…" :
                   purgeStatus?.status === "done" ? "Purge complete" :
                   "Delete every file shown on this page"}
                </h2>
                {purgeStatus?.status === "running" && purgeStatus.total > 0 && (
                  <>
                    <div className="mt-3 h-2 rounded-full bg-cream/15 overflow-hidden">
                      <div
                        className="h-full bg-gold transition-all duration-500"
                        style={{ width: `${Math.min(100, (purgeStatus.processed / purgeStatus.total) * 100)}%` }}
                      />
                    </div>
                    <div className="mt-2 text-xs text-cream/85 tabular-nums">
                      {purgeStatus.processed.toLocaleString()} / {purgeStatus.total.toLocaleString()} processed ·
                      <span className="text-gold ml-1">{fmtBytes(purgeStatus.bytes_freed || 0)} freed</span>
                    </div>
                  </>
                )}
                {purgeStatus?.status === "done" && (
                  <p className="mt-2 text-cream/75 text-sm">
                    Deleted {(purgeStatus.deleted || 0).toLocaleString()} files · freed {fmtBytes(purgeStatus.bytes_freed || 0)}.
                  </p>
                )}
                {purgeStatus?.status === "error" && (
                  <p className="mt-2 text-rose-200 text-sm">Error: {purgeStatus.error}</p>
                )}
                {(!purgeStatus || purgeStatus?.status === "idle" || purgeStatus?.status === "done") && data && (
                  <p className="mt-2 text-cream/70 text-sm leading-relaxed">
                    Use this only if you're sure you don't need any of the {data.orphan_count.toLocaleString()} orphan
                    files in the grid below. Runs server-side. Cannot be undone.
                  </p>
                )}
              </div>
              <div className="shrink-0">
                {purgeStatus?.status === "running" ? (
                  <button
                    onClick={loadPurgeStatus}
                    data-testid="purge-refresh"
                    className="inline-flex items-center gap-2 rounded-full bg-cream/10 border border-cream/20 text-cream px-5 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-cream/15"
                  >
                    <RefreshCw size={14} className="animate-spin" /> Live
                  </button>
                ) : (
                  <button
                    onClick={startBgPurge}
                    disabled={!data || data.orphan_count === 0}
                    data-testid="purge-start"
                    className="inline-flex items-center gap-2 rounded-full bg-rose-600 text-white px-6 py-3 text-xs font-bold uppercase tracking-wider hover:bg-rose-700 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Trash2 size={14} /> {purgeStatus?.status === "done" ? "Run again" : "Delete visible orphans"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </details>
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="inline-flex rounded-full border border-sand bg-white overflow-hidden">
          {[
            { id: "orphans", label: `Orphans${data ? ` (${data.orphan_count})` : ""}` },
            { id: "all", label: `All${data ? ` (${data.total_count})` : ""}` },
            { id: "referenced", label: `In use${data ? ` (${data.total_count - data.orphan_count})` : ""}` },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-testid={`filter-${f.id}`}
              className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-colors ${
                filter === f.id ? "bg-navy text-cream" : "text-ink/60 hover:bg-cream"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            disabled={loading}
            data-testid="storage-refresh"
            className="inline-flex items-center gap-2 rounded-full border border-sand text-ink/70 hover:text-navy px-4 py-2 text-xs font-semibold uppercase tracking-wider"
          >
            {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            Refresh
          </button>
          <button
            onClick={bulkDelete}
            disabled={selected.size === 0 || busy}
            data-testid="storage-bulk-delete"
            className="inline-flex items-center gap-2 rounded-full bg-rose-600 text-white px-5 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-rose-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
            Delete {selected.size > 0 ? selected.size : ""} · {fmtBytes(selectedSize)}
          </button>
        </div>
      </div>

      {/* Selection bar */}
      <div className="flex items-center gap-3 text-xs text-ink/60 mb-4">
        <button onClick={selectAllVisible} className="text-navy underline-offset-4 hover:underline" data-testid="select-all-visible">
          Select all visible ({visible.length})
        </button>
        {selected.size > 0 && (
          <button onClick={clearSelection} className="text-ink/55 underline-offset-4 hover:underline">
            Clear selection
          </button>
        )}
        {selectedReferenced > 0 && (
          <span className="inline-flex items-center gap-1 text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
            <AlertTriangle size={11} /> {selectedReferenced} still in use — will be skipped
          </span>
        )}
      </div>

      {/* Grid */}
      {loading ? (
        <div className="text-ink/40 py-20 text-center"><Loader2 className="animate-spin mx-auto" /></div>
      ) : visible.length === 0 ? (
        <div className="rounded-3xl bg-white border border-sand p-16 text-center">
          <CheckCircle2 className="mx-auto text-emerald-500" size={36} />
          <p className="mt-3 text-ink/60 text-sm">
            {filter === "orphans" ? "No orphan files — everything in storage is being used." : "Nothing to show."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {visible.map((it) => {
            const isImg = (it.content_type || "").startsWith("image/");
            const isSel = selected.has(it.id);
            return (
              <div
                key={it.id}
                onClick={() => toggle(it.id)}
                data-testid={`storage-item-${it.id}`}
                className={`relative group rounded-2xl overflow-hidden bg-white border-2 cursor-pointer transition-all ${
                  isSel ? "border-rose-500 ring-2 ring-rose-200" : it.referenced ? "border-emerald-400/50" : "border-sand hover:border-navy"
                }`}
              >
                <div className="aspect-square relative bg-sand/40">
                  {isImg ? (
                    <img src={it.url} alt={it.filename} loading="lazy" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-ink/40">
                      <FileText size={32} />
                    </div>
                  )}
                  {/* Selection checkbox */}
                  <div className="absolute top-2 left-2 w-7 h-7 rounded-md bg-white/95 backdrop-blur shadow-sm flex items-center justify-center">
                    {isSel ? <CheckSquare size={16} className="text-rose-600" /> : <Square size={16} className="text-ink/40" />}
                  </div>
                  {/* Status pill */}
                  {it.referenced ? (
                    <div className="absolute top-2 right-2 inline-flex items-center gap-1 bg-emerald-600/95 text-white text-[9px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full">
                      <CheckCircle2 size={10} /> In use
                    </div>
                  ) : (
                    <div className="absolute top-2 right-2 inline-flex items-center gap-1 bg-amber-500/95 text-white text-[9px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full">
                      Orphan
                    </div>
                  )}
                </div>
                <div className="p-3">
                  <div className="text-[11px] text-ink truncate" title={it.filename}>
                    {it.filename || it.id.slice(0, 12)}
                  </div>
                  <div className="text-[10px] text-ink/50 flex justify-between mt-0.5">
                    <span>{fmtBytes(it.size)}</span>
                    <span>{relTime(it.created_at)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Help footer */}
      <div className="mt-10 rounded-2xl bg-gold/10 border border-gold/30 p-5 text-sm text-ink/75 max-w-3xl">
        <strong className="text-navy">Safety net:</strong> we re-check references on the server before deleting. If something
        you tick is still referenced when you press Delete, it will be skipped automatically — your live site won't break.
        Items are deleted from both MongoDB and the Emergent Object Storage backend.
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, subtle, highlight }) {
  return (
    <div className={`rounded-2xl border p-5 flex items-start gap-3 ${highlight ? "bg-amber-50 border-amber-200" : "bg-white border-sand"}`}>
      <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${highlight ? "bg-amber-500 text-white" : "bg-navy/8 text-navy"}`}>
        <Icon size={18} />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.2em] text-ink/55 font-semibold">{label}</div>
        <div className="font-serif text-3xl text-ink tabular-nums leading-none mt-1">{value}</div>
        <div className="text-[11px] text-ink/55 mt-1">{subtle}</div>
      </div>
    </div>
  );
}
